import React from 'react';

export const PropertyConditions: React.FC = () => {
  const conditions = [
    {
      title: 'Well-Maintained Properties',
      description: 'If you have kept your apartment building in excellent condition with regular maintenance and updates, we recognize and value that care. Well-maintained properties with strong rent rolls and good tenant bases are attractive investments. We will offer fair market value that reflects the quality of your property and the income it generates.',
      icon: 'ri-home-smile-line'
    },
    {
      title: 'Properties Needing Repairs',
      description: 'Many apartment buildings require significant repairs or updates. Perhaps you have deferred maintenance, outdated systems, or cosmetic issues. We specialize in acquiring properties in any condition. You do not need to make repairs or improvements before selling. We will evaluate the property as-is and make an offer that accounts for needed work, saving you the time, money, and hassle of renovations.',
      icon: 'ri-tools-line'
    },
    {
      title: 'Problem Properties',
      description: 'Some apartment buildings come with challenges such as difficult tenants, code violations, management issues, or legal complications. These problems can make properties difficult to sell through traditional channels. We have the experience and resources to handle challenging situations. We can work through tenant issues, address code violations, and resolve complications that might scare away other buyers.',
      icon: 'ri-alert-line'
    },
    {
      title: 'Vacant Buildings',
      description: 'Vacant apartment buildings present unique challenges and opportunities. While they generate no income, they also offer flexibility for renovation or repositioning. We regularly purchase vacant buildings and understand how to value them appropriately. If your building is partially or fully vacant, we can still make a competitive offer based on its potential.',
      icon: 'ri-door-open-line'
    }
  ];

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            We Buy Properties in Any Condition
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            One of our key advantages is our ability to purchase apartment buildings regardless of their current condition. This flexibility benefits sellers who might struggle to find buyers through traditional channels.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {conditions.map((condition, index) => (
            <div key={index} className="bg-white rounded-xl p-8 shadow-sm">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className={`${condition.icon} text-3xl text-teal-600`}></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {condition.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {condition.description}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl p-12 shadow-sm">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Why Condition Flexibility Matters
          </h3>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-600 leading-relaxed">
            <p>
              Traditional buyers and conventional financing often require properties to meet certain condition standards. This can force sellers to invest thousands or even hundreds of thousands of dollars in repairs and improvements before they can sell. For many owners, especially those facing financial pressure or dealing with inherited properties, making these investments is not feasible or desirable.
            </p>
            <p>
              Our ability to purchase properties as-is eliminates this burden. You can sell your apartment building in its current condition without spending money on repairs, without managing contractors, and without the stress of renovation projects. We take on the responsibility of addressing any issues after we purchase the property.
            </p>
            <p>
              This approach is particularly valuable for sellers who have let maintenance slide due to financial constraints, health issues, or simply being overwhelmed by the demands of property management. Instead of feeling embarrassed about the condition of your property, you can work with a buyer who understands that buildings sometimes fall into disrepair and who is prepared to handle the situation professionally.
            </p>
            <p>
              Whether your apartment building is in pristine condition or needs substantial work, we encourage you to reach out. We will evaluate your property fairly and make an offer that reflects its current state and potential. Our goal is to provide a solution that works for you, regardless of the condition of your building.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
